<template>
  <div ref="viewerContainer" class="panorama-viewer"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, defineProps, defineEmits, defineExpose, withDefaults } from 'vue'
import { Viewer } from 'photo-sphere-viewer'
import 'photo-sphere-viewer/dist/photo-sphere-viewer.css'

const emit = defineEmits<{(e: 'load'): void, (e: 'error'): void}>()

interface Props {
  panorama: string;
  width?: string;
  height?: string;
  minFov?: number;
  maxFov?: number;
  defaultZoomLvl?: number;
  navbar?: (string | {name: string})[] | boolean;
  mousewheel?: boolean;
  touchmoveTwoFingers?: boolean;
  zoomSpeed?: number;
  allowZoom?: boolean;
  showNavbar?: boolean; // 新增：是否显示导航栏
}

const props = withDefaults(defineProps<Props>(), {
  width: '100%',
  height: '600px',
  minFov: 30,
  maxFov: 90,
  defaultZoomLvl: 70,
  navbar: false, // 默认不显示导航栏
  mousewheel: true,
  touchmoveTwoFingers: true,
  zoomSpeed: 1,
  allowZoom: true,
  showNavbar: false, // 默认隐藏导航栏
})

const viewerContainer = ref<HTMLElement | null>(null)
let viewer: Viewer | null = null
let keydownHandler: ((e: KeyboardEvent) => void) | null = null

const initViewer = () => {
  if (!viewerContainer.value || !props.panorama) return

  destroyViewer()

  if (!props.panorama.startsWith('http') && !props.panorama.startsWith('/')) {
    console.error('无效的全景图URL:', props.panorama)
    return
  }

  // 根据 showNavbar 属性决定是否显示导航栏
  const navbarConfig = props.showNavbar ? (props.navbar || []) : false

  viewer = new Viewer({
    container: viewerContainer.value,
    panorama: props.panorama,
    minFov: props.minFov,
    maxFov: props.maxFov,
    defaultZoomLvl: props.defaultZoomLvl,
    navbar: navbarConfig, // 动态控制导航栏
    mousewheel: props.mousewheel && props.allowZoom,
    touchmoveTwoFingers: props.touchmoveTwoFingers && props.allowZoom,
    zoomSpeed: props.zoomSpeed,
    moveInertia: true,
    caption: props.showNavbar ? '全景图' : '', // 只在显示导航栏时显示标题
    loadingTxt: '加载中...',
  })

  viewer.once('ready', () => {
    emit('load')
  })
  viewer.once('error', () => {
    emit('error')
  })

  // 键盘缩放事件
  keydownHandler = (e: KeyboardEvent) => {
    if (!viewer) return
    if (e.key === '+' || e.key === '=') {
      e.preventDefault()
      const currentFov = viewer.getZoomLevel()
      const newFov = Math.max(props.minFov, currentFov - 5)
      viewer.zoom(newFov)
      console.log('键盘放大:', currentFov, '->', newFov)
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault()
      const currentFov = viewer.getZoomLevel()
      const newFov = Math.min(props.maxFov, currentFov + 5)
      viewer.zoom(newFov)
      console.log('键盘缩小:', currentFov, '->', newFov)
    }
  }
  window.addEventListener('keydown', keydownHandler)

  // 添加双击缩放
  viewerContainer.value.addEventListener('dblclick', (e) => {
    if (!viewer || !props.allowZoom) return
    e.preventDefault()
    const rect = viewerContainer.value.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const currentFov = viewer.getZoomLevel()
    const newFov = Math.max(props.minFov, currentFov - 10)
    viewer.zoom(newFov, { x, y })
    console.log('双击放大:', currentFov, '->', newFov, 'at', x, y)
  })

  // 监听缩放事件
  viewer.on('zoom-updated', (e) => {
    console.log('缩放更新:', e.zoomLevel)
  })

  // 添加滚轮事件监听
  viewerContainer.value.addEventListener('wheel', (e) => {
    if (!props.mousewheel || !props.allowZoom) return
    console.log('滚轮事件 delta:', e.deltaY, '当前FOV:', viewer?.getZoomLevel())
  }, { passive: true })
}

const destroyViewer = () => {
  if (viewer) {
    viewer.off('zoom-updated')
    viewer.destroy()
    viewer = null
  }
  if (keydownHandler) {
    window.removeEventListener('keydown', keydownHandler)
    keydownHandler = null
  }
}

// 监听全景图URL变化
watch(() => props.panorama, (newVal) => {
  if (newVal && viewer) {
    console.log('切换全景图:', newVal)
    viewer.setPanorama(newVal)
  } else if (newVal) {
    initViewer()
  }
})

// 监听导航栏显示状态变化
watch(() => props.showNavbar, () => {
  initViewer()
})

// 监听关键配置变化
watch([
  () => props.minFov,
  () => props.maxFov,
  () => props.allowZoom,
  () => props.navbar
], () => {
  initViewer()
})

onMounted(() => {
  requestAnimationFrame(() => {
    initViewer()
  })
})

onBeforeUnmount(() => {
  destroyViewer()
})

defineExpose({
  zoomIn: () => {
    if (viewer) {
      const currentFov = viewer.getZoomLevel()
      const newFov = Math.max(props.minFov, currentFov - 5)
      viewer.zoom(newFov)
      console.log('API zoomIn:', currentFov, '->', newFov)
    }
  },
  zoomOut: () => {
    if (viewer) {
      const currentFov = viewer.getZoomLevel()
      const newFov = Math.min(props.maxFov, currentFov + 5)
      viewer.zoom(newFov)
      console.log('API zoomOut:', currentFov, '->', newFov)
    }
  },
  setZoomLevel: (level: number) => {
    if (viewer) {
      viewer.zoom(level)
      console.log('API setZoomLevel:', level)
    }
  },
  getZoomLevel: () => {
    const level = viewer?.getZoomLevel()
    console.log('API getZoomLevel:', level)
    return level
  },
  setPanorama: (url: string) => {
    if (viewer) {
      viewer.setPanorama(url)
      console.log('API setPanorama:', url)
    }
  },
  destroy: destroyViewer,
  toggleNavbar: () => {
    // 动态切换导航栏显示
    initViewer()
  }
})
</script>

<style scoped>
.panorama-viewer {
  width: v-bind(width);
  height: v-bind(height);
  position: relative;
  overflow: hidden;
  pointer-events: auto !important;
  z-index: 1;
  border: 2px solid transparent;
}

.panorama-viewer:hover {
  border-color: rgba(255, 255, 255, 0.2);
}

/* 完全隐藏导航栏 */
:deep(.psv-navbar) {
  display: none !important;
}

/* 隐藏加载动画（可选） */
:deep(.psv-loader) {
  display: none !important;
}

/* 隐藏标题（可选） */
:deep(.psv-caption) {
  display: none !important;
}

/* 容器样式 */
:deep(.psv-container) {
  z-index: 1 !important;
}

/* 鼠标样式 */
.panorama-viewer {
  cursor: grab;
}

.panorama-viewer:active {
  cursor: grabbing;
}

/* 错误提示样式 */
:deep(.psv-error) {
  background: rgba(255, 0, 0, 0.8) !important;
  color: white !important;
}

/* 全屏按钮样式（如果需要保留全屏功能） */
:deep(.psv-fullscreen-button) {
  position: absolute !important;
  top: 20px !important;
  right: 20px !important;
  width: 40px !important;
  height: 40px !important;
  background: rgba(0, 0, 0, 0.5) !important;
  border-radius: 50% !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  z-index: 10 !important;
}

:deep(.psv-fullscreen-button:hover) {
  background: rgba(0, 0, 0, 0.7) !important;
  transform: scale(1.1) !important;
}
</style>
